import CryptoJS from "crypto-js";
import {
  BASE_URL,
  getUserLocation,
  post,
  postFormDataPdf,
  postFormDataPdfReplace,
} from "../apis/apis";
import toastAlert from "./toastAlert";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import logoRequireSign from "../assets/logo.png";
import check from "../assets/checkmark.png";
import EditIcon from "@mui/icons-material/Edit";
import ApprovalIcon from "@mui/icons-material/Approval";

import {
  FormatItalic as ItalicIcon,
  Image as ImageIcon,
  Note as LicenseIcon,
  EmojiObjects as StampIcon,
} from "@mui/icons-material";
const SECRET_KEY = "your-secret-key"; // Replace with a secure key
const drawText = async (
  page,
  { text, x, y, fontSize, font, color, maxWidth, pdfDoc }
) => {
  try {
    const pdfFont = await pdfDoc.embedFont(font);

    // Calculate text height adjustments
    const fontHeight = fontSize * 1.2;
    const adjustedY = y + fontHeight;

    // Handle text wrapping
    const words = text.split(" ");
    let line = "";
    let currentY = adjustedY;

    for (const word of words) {
      const testLine = line + (line ? " " : "") + word;
      const textWidth = pdfFont.widthOfTextAtSize(testLine, fontSize);

      if (textWidth > maxWidth && line) {
        page.drawText(line, {
          x: x + 4, // Add small padding
          y: currentY,
          size: fontSize,
          font: pdfFont,
          color: color,
        });
        line = word;
        currentY -= fontHeight;
      } else {
        line = testLine;
      }
    }

    // Draw remaining text
    if (line) {
      page.drawText(line, {
        x: x + 4,
        y: currentY,
        size: fontSize,
        font: pdfFont,
        color: color,
      });
    }
  } catch (error) {
    console.error("Error drawing text:", error);
  }
};

const drawCheckmark = async (page, { x, y, size }) => {
  try {
    // Draw checkmark using vector paths
    page.drawSvgPath("M3,11 L7,14 L14,7", {
      x: x + size * 0.2,
      y: y + size * 0.2,
      borderWidth: 2,
      borderColor: rgb(0, 0, 0),
      scale: size / 20,
    });
  } catch (error) {
    console.error("Error drawing checkmark:", error);
  }
};

const drawImage = async (pdfDoc, page, { imageUrl, x, y, width, height, annotationHeight = null, pxToPdfScale = null, centerInBox = false }) => {
  try {
    let imageBytes;
    if (imageUrl.startsWith("data:")) {
      // Handle base64 images
      imageBytes = Uint8Array.from(atob(imageUrl.split(",")[1]), (c) =>
        c.charCodeAt(0)
      );
    } else {
      // Handle URL images
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      imageBytes = new Uint8Array(await response.arrayBuffer());
    }

    // Determine image type and embed accordingly
    // Check actual image data signature, not just URL
    let image;
    // Check for PNG signature: 89 50 4E 47 (PNG header)
    // Check for JPEG signature: FF D8 FF (JPEG header)
    const isPng = imageBytes.length >= 4 && imageBytes[0] === 0x89 && imageBytes[1] === 0x50 && imageBytes[2] === 0x4E && imageBytes[3] === 0x47;
    const isJpeg = imageBytes.length >= 3 && imageBytes[0] === 0xFF && imageBytes[1] === 0xD8 && imageBytes[2] === 0xFF;

    if (isPng) {
      image = await pdfDoc.embedPng(imageBytes);
    } else if (isJpeg) {
      image = await pdfDoc.embedJpg(imageBytes);
    } else {
      // Fallback: try PNG first, then JPEG
      try {
        image = await pdfDoc.embedPng(imageBytes);
      } catch (pngError) {
        image = await pdfDoc.embedJpg(imageBytes);
      }
    }

    // Draw image with preserved aspect ratio
    const { width: imgWidth, height: imgHeight } = image.scale(1);
    const aspectRatio = imgWidth / imgHeight;
    let drawWidth = width;
    let drawHeight = height;

    // Match editor behavior:
    // - object-fit: contain
    // - max-width/max-height (do not upscale smaller intrinsic images)
    let intrinsicWidthForPdf = imgWidth;
    let intrinsicHeightForPdf = imgHeight;
    if (typeof pxToPdfScale === "number" && Number.isFinite(pxToPdfScale) && pxToPdfScale > 0) {
      intrinsicWidthForPdf = imgWidth * pxToPdfScale;
      intrinsicHeightForPdf = imgHeight * pxToPdfScale;
    }
    const containScale = Math.min(
      width / intrinsicWidthForPdf,
      height / intrinsicHeightForPdf
    );
    const finalScale = Math.min(containScale, 1);
    drawWidth = intrinsicWidthForPdf * finalScale;
    drawHeight = intrinsicHeightForPdf * finalScale;

    // Position image at bottom of annotation box (matching text behavior)
    // y parameter is the TOP of the annotation box in PDF coordinates
    // If annotationHeight is provided, position from bottom (like text does)
    // page.drawImage draws from bottom-left corner
    let finalX = x;
    let finalY;
    if (annotationHeight && annotationHeight > 0) {
      if (centerInBox) {
        // Center image vertically within the annotation box.
        finalY = y - annotationHeight + (annotationHeight - drawHeight) / 2;
      } else {
        // Position at bottom of annotation box (y is top, so bottom = y - annotationHeight)
        const bottomMargin = 0;
        finalY = y - annotationHeight + bottomMargin;
      }
    } else {
      // Fallback: use y as bottom position (backward compatibility)
      finalY = y;
    }
    // Center the image horizontally inside the annotation box.
    // Keeps downloaded signatures aligned like the editor preview.
    if (width > drawWidth || centerInBox) {
      finalX = x + (width - drawWidth) / 2;
    }
    page.drawImage(image, {
      x: finalX,
      y: finalY, // Bottom of annotation box - image sits at bottom, matching text behavior
      width: drawWidth,
      height: drawHeight,
    });
  } catch (error) {
    console.error("Error drawing image:", error);
    throw error; // Re-throw to prevent silent failure
  }
};

const drawHighlight = (page, { x, y, width, height, color, opacity }) => {
  try {
    page.drawRectangle({
      x,
      y,
      width,
      height,
      color,
      opacity,
    });
  } catch (error) {
    console.error("Error drawing highlight:", error);
  }
};

// Helper function to convert coordinates
const convertCoordinates = (pageHeight, { x, y, height }) => {
  // Convert Y coordinate from top-left to bottom-left origin
  const pdfY = pageHeight - y - height;
  return { pdfX: x, pdfY };
};

// Helper function to calculate text dimensions
const calculateTextDimensions = (text, fontSize, font) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = `${fontSize}px ${font}`;
  const metrics = ctx.measureText(text);
  return {
    width: metrics.width,
    height: fontSize * 1.2,
  };
};
export const downloadPdfWithAnnotations = async (
  originalPdfUrl,
  annotations,
  fileName = "document.pdf"
) => {
  try {
    // Fetch the original PDF
    const pdfBytes = await fetch(originalPdfUrl).then((res) =>
      res.arrayBuffer()
    );
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();

    // Process annotations page by page (skip non-array entries e.g. _lastUpdate)
    for (const [pageNum, pageAnnotations] of Object.entries(annotations)) {
      if (!Array.isArray(pageAnnotations)) continue;
      const page = pages[pageNum - 1];
      if (!page) continue;
      const { width, height } = page.getSize();

      for (const annot of pageAnnotations) {
        // Convert relative positions to absolute PDF coordinates
        const pdfX = annot.x;
        const pdfY = height - annot.y - (annot.height || 0); // Flip Y coordinate

        switch (annot.type) {
          case "my_text":
          case "signer_text":
            await drawText(page, {
              text: annot.text || "",
              x: pdfX,
              y: pdfY,
              fontSize: annot.fontSize || 14,
              font: annot.format?.includes("bold")
                ? "Helvetica-Bold"
                : "Helvetica",
              color: rgb(0, 0, 0),
              maxWidth: annot.width,
              pdfDoc: pdfDoc,
            });
            break;

          case "checkmark":
          case "signer_checkmark":
            if (annot.checked) {
              await drawCheckmark(page, {
                x: pdfX,
                y: pdfY,
                size: annot.width || 14,
              });
            }
            break;

          case "date":
          case "signer_date":
            await drawText(page, {
              text: annot.text || "",
              x: pdfX,
              y: pdfY,
              fontSize: annot.fontSize || 14,
              font: "Helvetica",
              color: rgb(0, 0, 0),
            });
            break;

          case "my_signature":
          case "my_initials":
          case "stamp":
            if (annot.imageUrl) {
              await drawImage(pdfDoc, page, {
                imageUrl: annot.imageUrl,
                x: pdfX,
                y: pdfY,
                width: annot.width,
                height: annot.height,
              });
            }
            break;

          case "highlight":
            drawHighlight(page, {
              x: pdfX,
              y: pdfY,
              width: annot.width,
              height: annot.height,
              color: rgb(1, 1, 0),
              opacity: 0.3,
            });
            break;
        }
      }
    }

    // Save the modified PDF
    const modifiedPdfBytes = await pdfDoc.save();

    // Download the PDF
    const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error creating PDF:", error);
    return false;
  }
};
// Helper function to get StandardFont based on fontFamily and format
const getStandardFont = (fontFamily, format = []) => {
  const isBold = format.includes("bold");
  const isItalic = format.includes("italic");

  // Normalize font family name for comparison
  const normalizedFont = (fontFamily || "").toLowerCase().replace(/[^a-z]/g, "");

  // Map font families to StandardFonts
  // Support various naming conventions: "Times-Roman", "Times New Roman", "TimesRoman", etc.
  if (normalizedFont.includes("times") || normalizedFont.includes("roman")) {
    if (isBold && isItalic) return StandardFonts.TimesRomanBoldItalic;
    if (isBold) return StandardFonts.TimesRomanBold;
    if (isItalic) return StandardFonts.TimesRomanItalic;
    return StandardFonts.TimesRoman;
  } else if (normalizedFont.includes("courier")) {
    if (isBold && isItalic) return StandardFonts.CourierBoldOblique;
    if (isBold) return StandardFonts.CourierBold;
    if (isItalic) return StandardFonts.CourierOblique;
    return StandardFonts.Courier;
  } else {
    // Default to Helvetica (also matches "Helvetica", "Arial", etc.)
    if (isBold && isItalic) return StandardFonts.HelveticaBoldOblique;
    if (isBold) return StandardFonts.HelveticaBold;
    if (isItalic) return StandardFonts.HelveticaOblique;
    return StandardFonts.Helvetica;
  }
};

// Helper function to convert hex/rgba color to rgb
const parseColor = (colorString) => {
  if (!colorString) return rgb(0, 0, 0);

  // Handle rgba format: rgba(r, g, b, a)
  if (colorString.startsWith("rgba")) {
    const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return rgb(
        parseInt(match[1]) / 255,
        parseInt(match[2]) / 255,
        parseInt(match[3]) / 255
      );
    }
  }

  // Handle hex format: #RRGGBB or #RGB
  if (colorString.startsWith("#")) {
    const hex = colorString.slice(1);
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    return rgb(r, g, b);
  }

  // Default to black
  return rgb(0, 0, 0);
};

// Enhanced drawText function with alignment support
const drawTextWithAlignment = async (
  page,
  {
    text,
    x,
    y,
    fontSize,
    font,
    color,
    maxWidth,
    pdfDoc,
    alignment = "left",
    lineHeight = 1,
    annotationHeight = null, // Optional: height of annotation box for bottom-aligned text
    baselineOffsetDown = 0, // Optional: move baseline down (points) e.g. for date fields
    verticalAlign = "bottom", // "top" | "middle" | "center" | "bottom"
  }
) => {
  try {
    const pdfFont = await pdfDoc.embedFont(font);
    const fontHeight = fontSize * lineHeight;
    // PDF text is drawn from the baseline, not the top.
    // y is the TOP of the annotation box in PDF coordinates (Y decreases downward in PDF).
    // Prefer true ascent (exclude descender) so glyphs don't float above the box top.
    const ascentRaw = pdfFont.heightAtSize(fontSize, { descender: false });
    const ascent =
      ascentRaw > 0 ? ascentRaw : fontSize * 0.8;
    let currentY;
    if (verticalAlign === "top") {
      currentY = y - ascent - baselineOffsetDown;
    } else if (
      (verticalAlign === "middle" || verticalAlign === "center") &&
      annotationHeight &&
      annotationHeight > 0
    ) {
      // Midway between top-baseline and bottom-baseline.
      // Pure top floated above form labels; pure bottom sat too low — middle matches editor optical line.
      const bottomMargin = Math.min(3, fontSize * 0.1);
      const topBaseline = y - ascent;
      const bottomBaseline = y - annotationHeight + bottomMargin;
      currentY = (topBaseline + bottomBaseline) / 2 - baselineOffsetDown;
    } else if (annotationHeight && annotationHeight > 0) {
      // bottom (default): baseline near bottom of box (descender margin)
      const bottomMargin = Math.min(3, fontSize * 0.1);
      currentY = y - annotationHeight + bottomMargin - baselineOffsetDown;
    } else {
      // Fallback: top when no height
      currentY = y - ascent - baselineOffsetDown;
    }

    // Check if entire text fits on one line (like editor's nowrap behavior)
    const fullTextWidth = pdfFont.widthOfTextAtSize(text, fontSize);

    // Handle text wrapping - only wrap if text doesn't fit on one line
    const words = text.split(" ");
    let line = "";
    const lines = [];

    // If text fits on one line, don't wrap (match editor's nowrap behavior)
    if (fullTextWidth <= maxWidth) {
      lines.push(text); // Single line, no wrapping
    } else {
      // Text doesn't fit, wrap it
      for (const word of words) {
        const testLine = line + (line ? " " : "") + word;
        const textWidth = pdfFont.widthOfTextAtSize(testLine, fontSize);

        if (textWidth > maxWidth && line) {
          lines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }
      if (line) {
        lines.push(line);
      }
    }

    // Draw each line with proper alignment
    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i];
      const lineWidth = pdfFont.widthOfTextAtSize(lineText, fontSize);

      let drawX = x;
      if (alignment === "center") {
        drawX = x + (maxWidth - lineWidth) / 2;
      } else if (alignment === "right") {
        drawX = x + maxWidth - lineWidth;
      }

      page.drawText(lineText, {
        x: drawX,
        y: currentY,
        size: fontSize,
        font: pdfFont,
        color: color,
      });

      // Move down for next line (subtract because Y decreases downward in PDF)
      currentY -= fontHeight;
    }
  } catch (error) {
    console.error("Error drawing text:", error);
  }
};

// Main function to download PDF with text annotations
// options.editorPageWidthPx: width (px) at which the page is displayed in the editor; used to scale
// font size so downloaded PDF matches editor. If omitted, uses annot.pageWidth or fallback 700.
export const downloadPdfWithTextAnnotations = async (
  originalPdfUrl,
  annotations,
  fileName = "document.pdf",
  options = {}
) => {
  const editorPageWidthPx = options.editorPageWidthPx;
  const logFontScale = options.logFontScale !== false; // set to false to disable logging
  const disableMobileTextScale = options.disableMobileTextScale === true;
  // Optional export-only vertical nudge (points) for text fields.
  // This affects PDF download rendering only, never editor preview.
  const textVerticalNudgeUpPoints = Number(options.textVerticalNudgeUpPoints || 0);
  const authToken =
    options.authToken ||
    (typeof localStorage !== "undefined"
      ? (() => {
        try {
          const raw = localStorage.getItem("user_data");
          return raw ? JSON.parse(raw)?.token : null;
        } catch (_) {
          return null;
        }
      })()
      : null);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMobileBrowser = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);
  try {
    // Fetch the original PDF (Bearer needed for regional encrypted-view; US allows ?user_id=)
    const fetchOptions = { method: "GET" };
    if (
      authToken &&
      typeof originalPdfUrl === "string" &&
      originalPdfUrl.includes("/api/encrypted-")
    ) {
      fetchOptions.headers = { Authorization: `Bearer ${authToken}` };
    }
    const pdfBytes = await fetch(originalPdfUrl, fetchOptions).then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch PDF: ${res.status}`);
      }
      return res.arrayBuffer();
    });

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();

    // Filter to only supported annotation types for download
    // Text-like fields (including dropdown selections)
    const textTypes = [
      "my_text",
      "signer_text",
      "date",
      "signer_date",
      "signer_dropdown",
      "my_dropdown",
    ];
    // Image-like fields (including signer images/signatures)
    const imageTypes = [
      "my_signature",
      "my_initials",
      "stamp",
      "signer_initials",
      "signer_initials_text",
      "signer_chooseImgStamp",
      "signer_chooseImgDrivingL",
      "signer_chooseImgPassportPhoto",
    ];

    // Process annotations page by page (skip non-array entries e.g. _lastUpdate)
    for (const [pageNumStr, pageAnnotations] of Object.entries(annotations)) {
      if (!Array.isArray(pageAnnotations)) continue;
      const pageNum = parseInt(pageNumStr, 10);
      if (pageNum < 1 || pageNum > pages.length) {
        console.warn(`Page ${pageNum} does not exist in PDF`);
        continue;
      }

      const page = pages[pageNum - 1];
      const { width: pdfWidth, height: pdfHeight } = page.getSize();
      // Some PDFs define a CropBox that does not start at (0,0). The editor uses the
      // rendered/cropped view, so map percentages to the visible box, not full MediaBox.
      let visibleBox = { x: 0, y: 0, width: pdfWidth, height: pdfHeight };
      try {
        if (typeof page.getCropBox === "function") {
          const cropBox = page.getCropBox();
          if (
            cropBox &&
            Number.isFinite(cropBox.x) &&
            Number.isFinite(cropBox.y) &&
            Number.isFinite(cropBox.width) &&
            Number.isFinite(cropBox.height) &&
            cropBox.width > 0 &&
            cropBox.height > 0
          ) {
            visibleBox = cropBox;
          }
        }
      } catch (_) {
        // Keep full page as fallback if CropBox lookup fails.
      }

      // Deduplicate page annotations before drawing.
      // Some payloads can contain repeated date entries at the same position; drawing both
      // causes duplicated dates in the downloaded PDF.
      const seenIds = new Set();
      const seenDatePositionKeys = new Set();
      const dedupedPageAnnotations = [];
      let duplicateByIdCount = 0;
      let duplicateByDatePosCount = 0;
      for (const annot of pageAnnotations) {
        if (!annot || typeof annot !== "object") continue;

        if (annot.id !== undefined && annot.id !== null) {
          const idKey = String(annot.id);
          if (seenIds.has(idKey)) {
            duplicateByIdCount += 1;
            continue;
          }
          seenIds.add(idKey);
        }

        const isDateLike = annot.type === "date" || annot.type === "signer_date";
        if (isDateLike) {
          const x = Number(annot.x);
          const y = Number(annot.y);
          if (Number.isFinite(x) && Number.isFinite(y)) {
            const posKey = `${Math.round(x * 10) / 10}:${Math.round(y * 10) / 10}`;
            if (seenDatePositionKeys.has(posKey)) {
              duplicateByDatePosCount += 1;
              continue;
            }
            seenDatePositionKeys.add(posKey);
          }
        }

        dedupedPageAnnotations.push(annot);
      }
      // Process each annotation
      // Use annotation percentages directly on PDF dimensions (100% scale)
      // This is screen-size independent and ensures consistency between editor and download
      for (const annot of dedupedPageAnnotations) {
        // Convert percentage coordinates (0-100%) to the visible page box (CropBox/media fallback)
        // so placement matches what users see in the editor render.
        const pdfX = visibleBox.x + (annot.x / 100) * visibleBox.width;
        const pdfY = visibleBox.y + visibleBox.height - (annot.y / 100) * visibleBox.height;
        const pdfWidth_annot = (annot.width / 100) * visibleBox.width;
        const pdfHeight_annot = (annot.height / 100) * visibleBox.height;
        const maxWidth = pdfWidth_annot;

        // Handle image-based annotations (signature, initials, stamp)
        if (imageTypes.includes(annot.type) && annot.imageUrl) {
          // Prefer base64 if available, otherwise convert URL to base64
          let imageUrlToUse = annot.imageUrl;

          // If imageUrl is not base64, fetch it and convert to base64
          if (!annot.imageUrl.startsWith("data:")) {
            try {
              const response = await fetch(annot.imageUrl);
              if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
              }
              const blob = await response.blob();
              const reader = new FileReader();
              imageUrlToUse = await new Promise((resolve, reject) => {
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error("Error converting image URL to base64:", error);
              // Fall back to original URL (drawImage will try to fetch it)
              imageUrlToUse = annot.imageUrl;
            }
          }

          // Draw signature/initials/stamp image
          // Calculate top Y position (same as text annotations) for consistency
          // annot.y is percentage from top (0-100), annot.height is percentage height
          // Top Y in PDF = pdfHeight - (annot.y / 100) * pdfHeight
          // Then position from bottom using annotationHeight (like text does)
          const imageTopY = visibleBox.y + visibleBox.height - (annot.y / 100) * visibleBox.height;
          // Convert intrinsic image px to PDF points using same page-width scale as editor.
          const assumedEditorWidth = 700;
          const widthForScale =
            annot.pageWidth && annot.pageWidth > 0
              ? annot.pageWidth
              : typeof editorPageWidthPx === "number" && editorPageWidthPx > 0
                ? editorPageWidthPx
                : assumedEditorWidth;
          const pxToPdfScale = visibleBox.width / widthForScale;

          // Center signature/initial images in the annotation box (UI + PDF parity).
          // Stamps/ID photos keep bottom-ish contain via centerInBox false unless type is signature-like.
          const centerSignatureTypes = [
            "my_signature",
            "my_initials",
            "signer_initials",
            "signer_initials_text",
          ];
          const shouldCenterInBox = centerSignatureTypes.includes(annot.type);
          try {
            await drawImage(pdfDoc, page, {
              imageUrl: imageUrlToUse,
              x: pdfX,
              y: imageTopY, // Pass top Y, drawImage will position using annotationHeight
              width: pdfWidth_annot,
              height: pdfHeight_annot,
              annotationHeight: pdfHeight_annot,
              pxToPdfScale,
              centerInBox: shouldCenterInBox,
            });
          } catch (error) {
            console.error("Error drawing image:", error);
          }
          continue;
        }

        // Handle checkmark annotations
        if ((annot.type === "checkmark" || annot.type === "signer_checkmark") && annot.checked) {
          // Calculate top Y position for PDF coordinate system
          // annot.y is percentage from top (0-100)
          // Top Y in PDF = pdfHeight - (annot.y / 100) * pdfHeight
          // drawCheckmark draws at y + size * 0.2 (above the Y)
          // To position checkmark at top of box, we need: topY = checkmarkY + size * 0.2
          // So: checkmarkY = topY - size * 0.2
          const topY = visibleBox.y + visibleBox.height - (annot.y / 100) * visibleBox.height;
          // Use the minimum of width/height for square checkmarks — match editor preview size
          // (do not 2x "checkmark"; that made merge size/position look too big and low)
          const checkmarkSize = Math.min(pdfWidth_annot, pdfHeight_annot);
          const checkmarkY = topY - checkmarkSize * 0.2;
          await drawCheckmark(page, {
            x: pdfX,
            y: checkmarkY,
            size: checkmarkSize,
          });
          continue;
        }

        // Handle highlight annotations
        if (annot.type === "highlight") {
          const highlightY =
            visibleBox.y +
            visibleBox.height -
            ((annot.y + annot.height) / 100) * visibleBox.height;
          drawHighlight(page, {
            x: pdfX,
            y: highlightY,
            width: pdfWidth_annot,
            height: pdfHeight_annot,
            color: rgb(1, 1, 0),
            opacity: 0.3,
          });
          continue;
        }

        const textValue = String(annot.text ?? annot.selectedOption ?? "");

        // Skip non-text annotations or empty text
        if (!textTypes.includes(annot.type) || !textValue.trim()) {
          continue;
        }

        // Get font properties
        const fontFamily = annot.fontFamily || "Helvetica";
        const format = annot.format || [];
        const standardFont = getStandardFont(fontFamily, format);
        const color = parseColor(annot.color);

        // NEW STRATEGY: Scale font size proportionally based on page width
        // 
        // The font size in the editor was set relative to how the page was displayed.
        // We need to scale it to match the PDF page dimensions.
        //
        // If pageWidth was stored when annotation was created, use it for accurate scaling.
        // Otherwise, fall back to a reasonable default based on typical screen widths.
        const displayedFontSize = annot.fontSize || 21;

        // Use editor display width so downloaded font size matches what user sees.
        // Priority: annot.pageWidth (stored when created) > options.editorPageWidthPx (current view) > 700 fallback.
        const assumedEditorWidth = 700;
        const widthForScale =
          annot.pageWidth && annot.pageWidth > 0
            ? annot.pageWidth
            : typeof editorPageWidthPx === "number" && editorPageWidthPx > 0
              ? editorPageWidthPx
              : assumedEditorWidth;

        let fontSize = displayedFontSize * (visibleBox.width / widthForScale);

        // Apply mobile display scaling to PDF so downloaded fonts match what user sees on screen
        // Detect mobile: if editorPageWidthPx is exactly 800 (baseline), it's mobile download
        const isMobileDownload = editorPageWidthPx === 800;
        const isTextOrDateField = textTypes.includes(annot.type);
        const isDateFieldType = annot.type === "date" || annot.type === "signer_date";
        if (isMobileDownload && isTextOrDateField) {
          // On mobile display, fonts are scaled down
          // Apply the same scaling so PDF matches what user sees
          fontSize = fontSize * 0.82;
        }

        // Mobile browsers: scale non-date text fields to 48%
        if (
          isMobileBrowser &&
          isTextOrDateField &&
          !isDateFieldType &&
          !disableMobileTextScale
        ) {
          fontSize = fontSize * 0.48;
        }

        if (logFontScale && (annot.type === "signer_text" || annot.type === "signer_date" || annot.type === "my_text")) {
          console.log("[PDF font scale]", {
            type: annot.type,
            pageNum: pageNumStr,
            annotPageWidth: annot.pageWidth,
            editorPageWidthPx,
            widthForScale,
            pdfWidth: Math.round(pdfWidth),
            displayedFontSize,
            fontSizePts: Math.round(fontSize * 100) / 100,
          });
        }

        const alignment = annot.alignment || "left";
        const lineHeight = annot.lineHeight || 1;

        // Text and date fields should stay on one line in the downloaded PDF (match editor).
        // If the computed font size would cause wrapping, shrink it to fit within maxWidth.
        const singleLineTypes = ["date", "signer_date", "my_text", "signer_text"];
        if (singleLineTypes.includes(annot.type) && maxWidth > 0) {
          try {
            const pdfFontForMeasure = await pdfDoc.embedFont(standardFont);
            let oneLineWidth = pdfFontForMeasure.widthOfTextAtSize(textValue, fontSize);

            // Keep shrinking until text fits with a good safety margin (0.95 to account for any rendering differences)
            let iterations = 0;
            while (oneLineWidth > maxWidth && iterations < 10) {
              const fitRatio = maxWidth / oneLineWidth;
              // Apply more aggressive shrinking (0.95 safety margin) to ensure text fits
              fontSize = Math.max(4, fontSize * fitRatio * 0.95);
              oneLineWidth = pdfFontForMeasure.widthOfTextAtSize(textValue, fontSize);
              iterations++;
            }
          } catch (e) {
            // ignore measurement failures; fallback to existing behavior
            console.error("[PDF shrink-to-fit] Font measurement failed:", e);
          }
        }

        // Text + date: optical middle of box (between old bottom-too-low and top-too-high).
        // Dropdowns keep bottom + optional nudge. Zero nudge for middle so legacy nudges don't fight.
        const isFormLineText =
          annot.type === "my_text" ||
          annot.type === "signer_text" ||
          annot.type === "date" ||
          annot.type === "signer_date";
        const verticalAlign = isFormLineText ? "middle" : "bottom";
        const baselineOffsetDown =
          verticalAlign === "bottom" && textTypes.includes(annot.type)
            ? -textVerticalNudgeUpPoints
            : 0;

        await drawTextWithAlignment(page, {
          text: textValue,
          x: pdfX,
          y: pdfY,
          fontSize: fontSize,
          font: standardFont,
          color: color,
          maxWidth: maxWidth,
          pdfDoc: pdfDoc,
          alignment: alignment,
          lineHeight: lineHeight,
          annotationHeight: pdfHeight_annot,
          baselineOffsetDown,
          verticalAlign,
        });
      }
    }

    // Save the modified PDF
    const modifiedPdfBytes = await pdfDoc.save();

    if (options.returnBytes) {
      return modifiedPdfBytes;
    }

    // Download the PDF
    const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error creating PDF with annotations:", error);
    return false;
  }
};

export const formatDateUSA = (date, timeZoneOffset = 0) => {
  const dateFormat = new Date(date);
  const utcTime = dateFormat.getTime() + dateFormat.getTimezoneOffset() * 60000;
  const convertedDate = new Date(utcTime + timeZoneOffset * 60000);
  const month = (convertedDate.getMonth() + 1).toString().padStart(2, "0");
  const day = convertedDate.getDate().toString().padStart(2, "0");
  const year = convertedDate.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateInternational = (date, timeZoneOffset = 0) => {
  const dateFormat = new Date(date);
  const utcTime = dateFormat.getTime() + dateFormat.getTimezoneOffset() * 60000;
  const convertedDate = new Date(utcTime + timeZoneOffset * 60000);
  const day = convertedDate.getDate().toString().padStart(2, "0");
  const month = (convertedDate.getMonth() + 1).toString().padStart(2, "0");
  const year = convertedDate.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatDateCustomPickert = (dateTimeString) => {
  // Parse the date string
  const date = new Date(dateTimeString);

  // Options for formatting the date part
  const dateOptions = { month: "short", day: "numeric", year: "numeric" };
  const formattedDate = date.toLocaleDateString("en-US", dateOptions);

  // Options for formatting the time part
  const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
  const formattedTime = date.toLocaleTimeString("en-US", timeOptions);

  // Combine the formatted date and time
  return `${formattedDate}`;
};

const formatDateEditor = (dateString, format) => {
  let formattedDate = "";
  switch (format) {
    case "m/d/y":
      formattedDate = formatDateUSA(dateString);
      break;
    case "d/m/y":
      formattedDate = formatDateInternational(dateString);
      break;
    case "m-d-y":
      formattedDate = formatDateCustomPickert(dateString);
      break;
    default:
      formattedDate = formatDateUSA(dateString); // Default format
      break;
  }
  return formattedDate;
};

export const formatDateTimeActivityLog = (dateString) => {
  // Validate input
  if (!dateString || typeof dateString !== "string") {
    return "Invalid date";
  }

  // Try to split the input date string
  const [datePart, timePart] = dateString.split(" ");
  if (!datePart || !timePart) {
    return "Invalid date";
  }

  // Parse the date part
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) {
    return "Invalid date";
  }

  // Parse the time part
  const [time, offset] = timePart.split(/(?=[+-])/);
  if (!time) {
    return "Invalid date";
  }
  const [hour, minute] = time.split(":");
  if (!hour || !minute) {
    return "Invalid date";
  }

  // Define month names
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Adjust time to 12-hour format
  let hourInt = parseInt(hour, 10);
  if (isNaN(hourInt)) {
    return "Invalid date";
  }
  const isPM = hourInt >= 12;
  hourInt = hourInt % 12 || 12; // Convert to 12-hour format

  // Determine AM/PM
  const ampm = isPM ? "PM" : "AM";

  // Format the date and time
  const formattedMonth = monthNames[parseInt(month, 10) - 1];
  if (!formattedMonth) {
    return "Invalid date";
  }

  return `${formattedMonth} ${parseInt(day, 10)}, ${year}, ${String(
    hourInt
  ).padStart(2, "0")}:${minute} ${ampm}`;
};

const wrapText = (text, font, fontSize, maxWidth, columnKey) => {
  const words = text.split(" ");
  let lines = [];
  let currentLine = "";

  // Estimate character width ratio based on font size, typically around 0.5 to 0.6 of the font size
  const charWidthRatio = 0.5;

  // Calculate max characters based on column width and font size
  const maxCharsPerLine = Math.floor(maxWidth / (fontSize * charWidthRatio));

  // Determine max length dynamically based on column key
  const max_length =
    columnKey === "email" ? maxCharsPerLine : maxCharsPerLine + 3;

  // Wrapping logic based on max_length
  if (text.length > max_length) {
    while (text.length > max_length) {
      let part = text.slice(0, max_length);
      lines.push(part);
      text = text.slice(max_length);
    }
    if (text.length > 0) lines.push(text);
  } else {
    for (const word of words) {
      const testLine = currentLine + word + " ";
      const testWidth = font.widthOfTextAtSize(testLine, fontSize);
      if (testWidth > maxWidth) {
        lines.push(currentLine.trim());
        currentLine = word + " ";
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine.trim());
  }

  return lines;
};
const embedImages = async (
  pdfDoc,
  pages,
  textItems,
  scaleFactor,
  canvasWidth,
  canvasHeight
) => {
  console.log(textItems);
  console.log("canvasWidth", canvasWidth);
  const imagePromises = textItems.map(async (field) => {
    if (
      [
        "my_signature",
        "stamp",
        "my_initials",
        "signer_initials",
        "signer_initials_text",
        "signer_chooseImgDrivingL",
        "signer_chooseImgPassportPhoto",
        "signer_chooseImgStamp",
      ].includes(field.type)
    ) {
      const imgUrl = `${field.url}`;
      const imgResponse = await fetch(imgUrl);
      const imgBytes = await imgResponse.arrayBuffer();
      const contentType = imgResponse.headers.get("content-type");
      let img;
      if (contentType === "image/png") {
        img = await pdfDoc.embedPng(imgBytes);
      } else if (contentType === "image/jpeg" || contentType === "image/jpg") {
        img = await pdfDoc.embedJpg(imgBytes);
      } else {
        throw new Error("Unsupported image type: " + contentType);
      }

      return { img, field };
    }
    return null;
  });

  const images = await Promise.all(imagePromises);
  for (const image of images) {
    if (image) {
      const { img, field } = image;
      const { x, y, width, height, page_no } = field;
      const page = pages[page_no - 1];

      // Get the original image dimensions
      const imgDims = img.scale(1);
      const aspectRatio = imgDims.width / imgDims.height;

      // Calculate the new dimensions while maintaining aspect ratio
      let imgWidth = width;
      let imgHeight = height;

      if (width / height > aspectRatio) {
        imgWidth = height * aspectRatio; // Adjust width to fit height
      } else {
        imgHeight = width / aspectRatio; // Adjust height to fit width
      }

      // Center the image inside the bounding box
      const adjustedX = x + (width - imgWidth) / 2;
      const adjustedY =
        page.getHeight() - y - imgHeight - (height - imgHeight) / 2;

      console.log("Placing element at PDF coordinates:", adjustedX, adjustedY);

      // Draw the image with the adjusted dimensions and position
      page.drawImage(img, {
        x: adjustedX,
        y: adjustedY,
        width: imgWidth,
        height: imgHeight,
      });
    }
  }
};
const getFont = async (pdfDoc, fontFamily, fontStyle) => {
  if (fontFamily === "Times New Roman") {
    return fontStyle === "italic"
      ? await pdfDoc.embedFont(StandardFonts.TimesRomanItalic)
      : await pdfDoc.embedFont(StandardFonts.TimesRoman);
  } else if (fontFamily === "Courier New") {
    return fontStyle === "italic"
      ? await pdfDoc.embedFont(StandardFonts.CourierOblique)
      : await pdfDoc.embedFont(StandardFonts.Courier);
  } else if (fontFamily === "Helvetica") {
    return fontStyle === "italic"
      ? await pdfDoc.embedFont(StandardFonts.HelveticaOblique)
      : await pdfDoc.embedFont(StandardFonts.Helvetica);
  }
  return fontStyle === "italic"
    ? await pdfDoc.embedFont(StandardFonts.HelveticaOblique)
    : await pdfDoc.embedFont(StandardFonts.Helvetica);
};
const isCertificate = (pageWidth, pageHeight) => {
  // Check common certificate aspect ratios and dimensions
  const aspectRatio = pageWidth / pageHeight;

  // Landscape certificates: Width is larger than height with ~4:3 ratio
  const isLandscape =
    aspectRatio > 1.3 &&
    aspectRatio < 1.5 &&
    pageWidth >= 800 &&
    pageHeight >= 600;

  // Portrait certificates: Height is larger than width with ~3:4 ratio
  const isPortrait =
    aspectRatio > 0.7 &&
    aspectRatio < 0.8 &&
    pageWidth >= 600 &&
    pageHeight >= 800;

  // Return true if either condition matches
  return isLandscape || isPortrait;
};

// const drawText = async (pdfDoc, pages, textItems) => {
//   try {
//     // Group annotations by page
//     const annotationsByPage = textItems.reduce((acc, item) => {
//       const pageNum = item.page_no || 1;
//       if (!acc[pageNum]) acc[pageNum] = [];
//       acc[pageNum].push(item);
//       return acc;
//     }, {});

//     // Process each page
//     for (const [pageNum, annotations] of Object.entries(annotationsByPage)) {
//       const page = pages[pageNum - 1];
//       const { width: pageWidth, height: pageHeight } = page.getSize();

//       for (const annotation of annotations) {
//         await drawAnnotation(pdfDoc, page, annotation, pageHeight, pageWidth);
//       }
//     }
//   } catch (error) {
//     console.error("Error in drawText:", error);
//   }
// };

const drawAnnotation = async (
  pdfDoc,
  page,
  annotation,
  pageHeight,
  pageWidth
) => {
  const {
    type,
    x,
    y,
    width,
    height,
    text,
    fontSize = 14,
    format = [],
    backgroundColor,
    checked,
  } = annotation;

  // Calculate correct position
  const yPos = pageHeight - y - height;

  try {
    switch (type) {
      case "my_text":
      case "signer_text":
        await drawTextField(pdfDoc, page, {
          ...annotation,
          y: yPos,
          pageHeight,
          pageWidth,
        });
        break;

      // case "checkmark":
      // case "signer_checkmark":
      //   if (checked) {
      //     await drawCheckbox(pdfDoc, page, {
      //       ...annotation,
      //       y: yPos,
      //     });
      //   }
      //   break;

      // case "date":
      // case "signer_date":
      //   await drawDateField(pdfDoc, page, {
      //     ...annotation,
      //     y: yPos,
      //   });
      //   break;

      // case "highlight":
      //   drawHighlight(page, {
      //     ...annotation,
      //     y: yPos,
      //   });
      //   break;
    }
  } catch (error) {
    console.error(`Error drawing annotation type ${type}:`, error);
  }
};

const drawTextField = async (pdfDoc, page, field) => {
  const { x, y, text, fontSize, format, width, pageWidth } = field;
  const font = await pdfDoc.embedFont(
    format?.includes("bold")
      ? StandardFonts.HelveticaBold
      : StandardFonts.Helvetica
  );

  // Handle text wrapping
  const words = text?.split(" ") || [];
  let line = "";
  let yOffset = y;
  const lineHeight = fontSize * 1.2;

  for (const word of words) {
    const testLine = line + (line ? " " : "") + word;
    const textWidth = font.widthOfTextAtSize(testLine, fontSize);

    if (textWidth > width && line) {
      page.drawText(line, {
        x: x + 4,
        y: yOffset,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
      line = word;
      yOffset -= lineHeight;
    } else {
      line = testLine;
    }
  }

  // Draw remaining text
  if (line) {
    page.drawText(line, {
      x: x + 4,
      y: yOffset,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }
};
// download end

const validateHeight = (height) => {
  return typeof height === "number" && !isNaN(height) ? height : 40; // fallback to 50 if invalid
};

const formatEventText = (eventText) => {
  const formattedText = eventText
    .toLowerCase() // Convert to lowercase
    .replace(/-/g, " "); // Replace dashes with spaces

  return formattedText.charAt(0).toUpperCase() + formattedText.slice(1); // Capitalize the first letter
};

export const hashIds = (ids) => {
  try {
    const idsString = JSON.stringify(ids);
    const encrypted = CryptoJS.AES.encrypt(idsString, SECRET_KEY).toString();
    return encodeURIComponent(encrypted);
  } catch (error) {
    console.error("Error hashing IDs:", error);
    return null;
  }
};

export const unhashIds = (hashedIds) => {
  try {
    const decoded = decodeURIComponent(hashedIds);
    const decrypted = CryptoJS.AES.decrypt(decoded, SECRET_KEY);
    const idsString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(idsString);
  } catch (error) {
    console.error("Error unhashing IDs:", error);
    return null;
  }
};

/** Force .pdf extension so Word-origin titles do not save/upload as .docx with PDF bytes. */
export const ensurePdfFileName = (name) => {
  const raw = (name && String(name).trim()) || "document.pdf";
  const officeExt = /\.(docx?|xlsx?|pptx?)$/i;
  if (officeExt.test(raw)) {
    return raw.replace(officeExt, ".pdf");
  }
  if (!/\.pdf$/i.test(raw)) {
    return `${raw}.pdf`;
  }
  return raw;
};

export const downloadOriginalPdf = async (
  pdfUrl,
  fileName = "document.pdf"
) => {
  try {
    const response = await fetch(pdfUrl);
    if (!response.ok) {
      throw new Error("PDF download failed");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Use provided filename or extract from URL; always .pdf for PDF downloads
    const rawName = fileName || pdfUrl.split("/").pop() || "document.pdf";
    const downloadName = ensurePdfFileName(rawName);
    link.download = downloadName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error downloading original PDF:", error);
    return false;
  }
};
export const saveData = async (file_id, annotations, chkmodal) => {
  try {
    const location = await getUserLocation();

    // Convert annotations object to proper format for JSONB
    const formattedAnnotations = [];

    // Process each page's annotations (skip non-array entries e.g. _lastUpdate)
    Object.entries(annotations).forEach(([pageNum, pageAnnotations]) => {
      if (!Array.isArray(pageAnnotations)) return;
      pageAnnotations.forEach((annot) => {
        // Create a clean annotation object (signer_date: always empty text for backend)
        const cleanAnnotation = {
          id: annot.id,
          type: annot.type,
          page_no: parseInt(pageNum),
          x: annot.x,
          y: annot.y,
          width: annot.width || 150,
          height: annot.height || 30,
          text: annot.type === "signer_date" ? "" : (annot.text || null),
          checked: annot.checked || false,
          imageUrl: annot.imageUrl || null,
          format: annot.format || [],
          fontSize: annot.fontSize || 14,
          fontFamily: annot.fontFamily || "Helvetica",
          options: annot.options || [],
          selectedOption: annot.selectedOption || null,
          backgroundColor: annot.backgroundColor || null,
          borderColor: annot.borderColor || null,
          signerId: annot.signerId || null,
          signerColor: annot.signerColor || null,
          isRequired: annot.isRequired || false,
          fieldLabel: annot.fieldLabel || null,
        };

        formattedAnnotations.push(cleanAnnotation);
      });
    });
    console.log(formattedAnnotations);
    console.log("file_id", file_id);
    const postData = {
      file_id: file_id,
      last_change: location?.date,
      position_array: formattedAnnotations, // Convert to JSON string for JSONB
    };

    const apiData = await post("file/saveCanvasDataWithFile_Id", postData);
    console.log("apiData", apiData);
    if (apiData.error) {
      toastAlert("error", apiData.message);
      return false;
    }
    if (chkmodal === true) {
      return {
        error: false,
        formatted_data: formattedAnnotations,
      };
    } else {
      toastAlert("success", "Document saved successfully!");
      return {
        error: false,
        formatted_data: formattedAnnotations,
      };
    }
  } catch (error) {
    console.error("Error saving data:", error);
    toastAlert("error", "Failed to save document");
    // return false;
    return {
      error: true,
    };
  }
};
export const saveDataTemplat = async (file_id, annotations, chkmodal) => {
  try {
    const location = await getUserLocation();

    // Convert annotations object to proper format for JSONB
    const formattedAnnotations = [];

    // Process each page's annotations (skip non-array entries e.g. _lastUpdate)
    Object.entries(annotations).forEach(([pageNum, pageAnnotations]) => {
      if (!Array.isArray(pageAnnotations)) return;
      pageAnnotations.forEach((annot) => {
        // Create a clean annotation object (signer_date: always empty text for backend)
        const cleanAnnotation = {
          id: annot.id,
          type: annot.type,
          page_no: parseInt(pageNum),
          x: annot.x,
          y: annot.y,
          width: annot.width || 150,
          height: annot.height || 30,
          text: annot.type === "signer_date" ? "" : (annot.text || null),
          checked: annot.checked || false,
          imageUrl: annot.imageUrl || null,
          format: annot.format || [],
          fontSize: annot.fontSize || 14,
          fontFamily: annot.fontFamily || "Helvetica",
          options: annot.options || [],
          selectedOption: annot.selectedOption || null,
          backgroundColor: annot.backgroundColor || null,
          borderColor: annot.borderColor || null,
          signerId: annot.signerId || null,
          signerColor: annot.signerColor || null,
          isRequired: annot.isRequired || false,
          fieldLabel: annot.fieldLabel || null,
        };

        formattedAnnotations.push(cleanAnnotation);
      });
    });
    console.log(formattedAnnotations);
    console.log("file_id", file_id);
    const postData = {
      template_id: file_id,
      // last_change: location?.date,
      position_array: formattedAnnotations, // Convert to JSON string for JSONB
    };

    const apiData = await post(
      "template/saveCanvasDataWithtemplateId",
      postData
    );
    console.log("apiData", apiData);
    if (apiData.error) {
      toastAlert("error", apiData.message);
      return false;
    }
    if (chkmodal === true) {
      return {
        error: false,
        formatted_data: formattedAnnotations,
      };
    } else {
      // toastAlert("success", "Document saved successfully!");
      return {
        error: false,
        formatted_data: formattedAnnotations,
      };
    }
  } catch (error) {
    console.error("Error saving data:", error);
    toastAlert("error", "Failed to save document");
    // return false;
    return {
      error: true,
    };
  }
};
// export const saveData = async (file_id, textItems) => {
//   // setUnsavedChanges(false);
//   // setsaveLoading(true);
//   console.log("Save Data To canvas ");
//   console.log(textItems);
//   console.log(file_id);
//   const location = await getUserLocation();
//   // const formattedItems = Array.isArray(textItems);
//   const postData = {
//     file_id: file_id,
//     last_change: location?.date,

//     position_array: textItems,
//     // position_array: JSON.stringify(formattedItems), // Convert to JSON string for JSONB,
//   };
//   try {
//     const apiData = await post("file/saveCanvasDataWithFile_Id", postData); // Specify the endpoint you want to call
//     console.log("Save Data To canvas ");

//     console.log(apiData);
//     if (apiData.error) {
//       toastAlert("error", apiData.message);
//       // setsaveLoading(false);
//       // setSaveSuccess(false);
//     } else {
//       toastAlert("success", "Document saved successfully!");

//       // setSaveSuccess(true);
//       // setTimeout(() => {
//       //   setSaveSuccess(false);
//       //   setsaveLoading(false);
//       //   setContinueModal(false);
//       // }, 2000);
//       // fetchDataPositions(file_id);
//       // UpdateLastChange();
//     }
//   } catch (error) {
//     console.log("Error fetching data:", error);
//     //   setsaveLoading(false);
//   }
// };
export const AddSignersData = async (file_id, signersData) => {
  // setLoadingSignersSave(true);
  //console.log(signersData);
  //console.log(file_id);

  const postData = {
    file_id: file_id,
    signers: signersData,
  };
  try {
    const apiData = await post("file/add-signer", postData); // Specify the endpoint you want to call
    console.log("Signers ");

    console.log(apiData);
    if (apiData.error) {
      // toastAlert("error", apiData.message);
      // setSignersData([]);
      // setLoadingSignersSave(false);
    } else {
      // setSignersData(apiData.data);
      // setSelectedSigner(apiData.data[apiData.data.length - 1]);
      // setCount(apiData.data.length);
      // setLoadingSignersSave(false);
      // fetchSignerData(file_id);
      // setSignerAddEdit(false);
      // setActive("2");
    }
  } catch (error) {
    // toastA
    console.log("Error fetching data:", error);
    //   setLoadingSignersSave(false);
  }
};
const getColorIndex = (color) => {
  if (!color) return 0;
  const baseColors = [
    "rgb(255 226 139",
    "rgb(192 165 207",
    "rgb(151 201 191",
    "rgb(247 185 148",
    "rgb(207 219 127",
    "rgb(255 153 128",
    "rgb(255 179 198",
    "rgb(230 198 230",
  ];

  // Remove opacity part and whitespace for comparison
  try {
    // Remove opacity part and whitespace for comparison
    const baseColor = color.includes("/")
      ? color.split("/")[0].trim()
      : color.trim();
    return baseColors.findIndex((c) => baseColor.startsWith(c));
  } catch (error) {
    return 0; // Return first color index if there's an error
  }
};
export const getColorWithNewOpacity = (color, isRequired) => {
  // console.log("fff", color);
  if (!color) {
    const defaultColor = "rgb(192 165 207";
    // Required fields: 70% opacity (more visible/opaque)
    // Non-required fields: 50% opacity (less visible/more transparent)
    return `${defaultColor} / ${isRequired ? "70%" : "50%"})`;
  }
  const colorIndex = getColorIndex(color);
  // Return color with new opacity based on index
  const baseColors = [
    "rgb(255 226 139",
    "rgb(192 165 207",
    "rgb(151 201 191",
    "rgb(247 185 148",
    "rgb(207 219 127",
    "rgb(255 153 128",
    "rgb(255 179 198",
    "rgb(230 198 230",
  ];
  // const baseColors = [
  //   "rgb(255 240 190 )", // Lighter yellow
  //   "rgb(220 205 230 )", // Lighter purple
  //   "rgb(190 225 215 )", // Lighter teal
  //   "rgb(250 215 190 )", // Lighter peach
  //   "rgb(230 238 180 )", // Lighter lime
  //   "rgb(255 190 175 )", // Lighter coral
  //   "rgb(255 210 225 )", // Lighter pink
  //   "rgb(240 220 240 )",
  // ];

  // Required fields: 70% opacity (more visible/opaque)
  // Non-required fields: 50% opacity (less visible/more transparent)
  return `${baseColors[colorIndex]} / ${isRequired ? "70%" : "50%"})`;
};

/**
 * Same as getColorWithNewOpacity but with custom opacity percentages (e.g. 80% required, 60% optional).
 * Use for public-form so PDF background is visible.
 */
export const getColorWithCustomOpacity = (color, isRequired, requiredPct = 80, optionalPct = 60) => {
  if (!color) {
    const defaultColor = "rgb(192 165 207";
    return `${defaultColor} / ${isRequired ? `${requiredPct}%` : `${optionalPct}%`})`;
  }
  const colorIndex = getColorIndex(color);
  const baseColors = [
    "rgb(255 226 139",
    "rgb(192 165 207",
    "rgb(151 201 191",
    "rgb(247 185 148",
    "rgb(207 219 127",
    "rgb(255 153 128",
    "rgb(255 179 198",
    "rgb(230 198 230",
  ];
  return `${baseColors[colorIndex]} / ${isRequired ? `${requiredPct}%` : `${optionalPct}%`})`;
};

/**
 * Date/signer_date preview layout: cap single-line height but keep the box BOTTOM fixed
 * so the field stays on the printed underline (do not shrink height while keeping old top).
 *
 * @param {object} opts
 * @param {number} opts.pixelY - top of stored annotation box (px)
 * @param {number} opts.pixelHeight - full stored annotation height (px)
 * @param {number} [opts.fontSize=15]
 * @param {boolean} [opts.isMobile=false]
 * @param {number|null} [opts.mobileTextHeight=null] - when set on mobile, use this height
 * @returns {{ top: number, height: number }}
 */
export const getDateFieldBoxLayout = ({
  pixelY,
  pixelHeight,
  fontSize = 15,
  isMobile = false,
  mobileTextHeight = null,
}) => {
  const safeY = Number.isFinite(pixelY) ? pixelY : 0;
  const safeH = Number.isFinite(pixelHeight) && pixelHeight > 0 ? pixelHeight : 22;
  const dateLineHeightPx = (Number(fontSize) || 15) * 1.4;
  const dateCappedHeight = Math.min(safeH, Math.max(22, dateLineHeightPx));

  let height = safeH;
  if (isMobile && mobileTextHeight != null && Number.isFinite(mobileTextHeight)) {
    height = mobileTextHeight;
  } else if (!isMobile) {
    height = dateCappedHeight;
  }

  // Keep bottom edge of stored box; raise top when height is reduced.
  const top = safeY + Math.max(0, safeH - height);
  return { top, height };
};

/**
 * Apply alpha to any color string (for public-form field backgrounds so PDF shows through).
 */
export const colorWithAlpha = (colorStr, alpha) => {
  if (alpha == null || alpha > 1) alpha = 0.8;
  if (!colorStr || typeof colorStr !== "string") {
    return `rgba(255, 226, 139, ${alpha})`;
  }
  const trim = colorStr.trim();
  const rgbaMatch = trim.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)/);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
  }
  const rgbSpaceMatch = trim.match(/rgb\s*\(\s*(\d+)\s+(\d+)\s+(\d+)(?:\s+\/\s*[\d.]+%?)?\s*\)/);
  if (rgbSpaceMatch) {
    return `rgba(${rgbSpaceMatch[1]}, ${rgbSpaceMatch[2]}, ${rgbSpaceMatch[3]}, ${alpha})`;
  }
  const hexMatch = trim.match(/^#([0-9a-fA-F]{6})$/);
  if (hexMatch) {
    const r = parseInt(hexMatch[1].slice(0, 2), 16);
    const g = parseInt(hexMatch[1].slice(2, 4), 16);
    const b = parseInt(hexMatch[1].slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return colorStr;
};

const fontSize = 9;
const margin = 20;
const lineHeight = 20;
const columnProportions = {
  event: 0.24, // 1/3 of the available space
  email: 0.27, // 1/3 of the available space
  ip_details: 0.61, // Combine IP address, Date, and Country into one column
};
// get apis
export const handleDownloadPDFHereTest1 = async ({
  setDownloadPdfLoader,
  imageUrls,
  textItems,
  canvasWidth,
  UniqIdDoc,
  ActivityLogData,
  fileName,
  file_id,
  statusData,
  imageUrlsCount,
  user_id,
  doc_completed,
  logo_data,
  noDec,
}) => {
  // console.log(ActivityLogData)
  try {
    // setDownloadPdfLoader(true);
    // console.log("ActivityLogData");
    // console.log(ActivityLogData);
    // const response = await fetch(`${imageUrls}`);
    // const existingPdfBytes = await response.arrayBuffer();
    // const pdfDoc = await PDFDocument.load(existingPdfBytes);
    // const pages = pdfDoc.getPages();
    const pdfBytes = await fetch(imageUrls).then((res) => res.arrayBuffer());

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const { height: pageHeight, width: pageWidth } = pages[0].getSize();
    // Load the PDF document
    const xOffset = 10;
    const yOffset = pageHeight - 20;
    const uniqId = UniqIdDoc;
    // start

    // end
    if (UniqIdDoc !== "null") {
      pages.forEach((page) => {
        page.drawText(`Document ID: ${uniqId}`, {
          x: xOffset,
          y: yOffset,
          size: 10,
          font,
          color: rgb(0, 0, 0),
        });
      });
    }

    // const pdfWidth = pages[0].getWidth();
    // const scaleFactor = pdfWidth / canvasWidth;
    // if (!Array.isArray(textItems)) {
    //   throw new Error("textItems is not an array or is undefined");
    // }
    // await embedImages(pdfDoc, pages, textItems, scaleFactor);
    await drawText(pdfDoc, pages, textItems);

    const modifiedPdfBytes = await pdfDoc.save();
    console.log("textItems");

    console.log(textItems);
    // if (statusData === "receiver") {
    //   await uploadModifiedPdfReceiver(
    //     modifiedPdfBytes,
    //     file_id,
    //     user_id,
    //     fileName,
    //     noDec
    //   );
    // } else {
    //   await uploadModifiedPdf(modifiedPdfBytes, file_id, user_id, fileName);
    // }
    downloadPDF(modifiedPdfBytes, fileName);
    // setDownloadPdfLoader(false);
  } catch (error) {
    // setDownloadPdfLoader(false);

    console.log("Error downloading PDF:", error);

    return {
      error: true,
    };
    // setDownloadPdfLoader(false);
    // console.log("Error downloading PDF:", error);
  }
};
// correct
export const handleDownloadPDFHereTest = async ({
  setDownloadPdfLoader,
  imageUrls,
  textItems,
  canvasWidth,
  UniqIdDoc,
  ActivityLogData,
  fileName,
  file_id,
  statusData,
  imageUrlsCount,
  user_id,
  doc_completed,
  logo_data,
  noDec,
  textBottomPaddingAbs: textBottomPaddingAbsParam,
}) => {
  try {
    console.log("Hanle pdf test  FFFHHHHHH");
    console.log("textItems", textItems);
    const pdfResponse = await fetch(imageUrls);
    if (!pdfResponse.ok) {
      console.error("Failed to fetch PDF:", pdfResponse.statusText);
      // Continue anyway with whatever response we got
    }
    const pdfArrayBuffer = await pdfResponse.arrayBuffer();
    const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

    // Build per-page visible box metrics (CropBox-aware) so backend route receives
    // coordinates in full-page percentages that match editor placement.
    const pageMetrics = {};
    try {
      const sourcePdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const sourcePages = sourcePdfDoc.getPages();
      sourcePages.forEach((page, index) => {
        const pageNum = index + 1;
        const { width: pageWidth, height: pageHeight } = page.getSize();
        let visibleBox = { x: 0, y: 0, width: pageWidth, height: pageHeight };
        try {
          if (typeof page.getCropBox === "function") {
            const cropBox = page.getCropBox();
            if (
              cropBox &&
              Number.isFinite(cropBox.x) &&
              Number.isFinite(cropBox.y) &&
              Number.isFinite(cropBox.width) &&
              Number.isFinite(cropBox.height) &&
              cropBox.width > 0 &&
              cropBox.height > 0
            ) {
              visibleBox = cropBox;
            }
          }
        } catch (_) {
          // Keep full page if CropBox read fails.
        }
        pageMetrics[pageNum] = {
          pageWidth,
          pageHeight,
          visibleBox,
        };
      });
    } catch (error) {
      console.warn("Failed to read source PDF page metrics, falling back to raw percentages:", error);
    }

    const annotationsArray = [];
    // Flatten the page-grouped annotations into a single array
    Object.entries(textItems).forEach(([pageNum, annotations]) => {
      const pageNumInt = parseInt(pageNum, 10);
      const metrics = pageMetrics[pageNumInt];
      annotations.forEach((annot) => {
        const rawX = Number(annot.x);
        const rawY = Number(annot.y);
        const rawWidth = Number(annot.width);
        const rawHeight = Number(annot.height);

        let backendX = rawX;
        let backendY = rawY;
        let backendWidth = rawWidth;
        let backendHeight = rawHeight;

        // Convert from visible-box percentages (editor) to full-page percentages (backend draw route).
        if (
          metrics &&
          Number.isFinite(rawX) &&
          Number.isFinite(rawY) &&
          Number.isFinite(rawWidth) &&
          Number.isFinite(rawHeight)
        ) {
          const { pageWidth, pageHeight, visibleBox } = metrics;
          if (pageWidth > 0 && pageHeight > 0) {
            const absLeft = visibleBox.x + (rawX / 100) * visibleBox.width;
            const absTop = visibleBox.y + visibleBox.height - (rawY / 100) * visibleBox.height;
            const absWidth = (rawWidth / 100) * visibleBox.width;
            const absHeight = (rawHeight / 100) * visibleBox.height;

            backendX = (absLeft / pageWidth) * 100;
            backendY = ((pageHeight - absTop) / pageHeight) * 100;
            backendWidth = (absWidth / pageWidth) * 100;
            backendHeight = (absHeight / pageHeight) * 100;
          }
        }

        // Pass signature/initial image positions through without any Y-shift.
        // The backend centers images in the annotation box; no pre-compensation needed.
        const bottomAlignImageTypes = [
          "my_signature",
          "my_initials",
          "signer_initials",
          "signer_initials_text",
        ];

        // Text-only vertical adjustment for merged/downloaded PDF:
        // positive textBottomPaddingAbs should move text UP from bottom line.
        if (
          annot.type === "my_text" ||
          annot.type === "signer_text" ||
          annot.type === "date" ||
          annot.type === "signer_date"
        ) {
          const textPaddingPx = Number.isFinite(textBottomPaddingAbsParam)
            ? textBottomPaddingAbsParam
            : 2.5; // px on PDF page space
          const pageHeightForNudge = metrics?.pageHeight || 1;
          const textBottomPaddingPercent =
            (textPaddingPx / pageHeightForNudge) * 100;
          backendY = Math.min(
            100,
            Math.max(0, backendY - textBottomPaddingPercent)
          );
        }

        const imageUrlForMerge =
          bottomAlignImageTypes.includes(annot.type) && annot.croppedImageData
            ? annot.croppedImageData
            : annot.imageUrl;

        // Add each annotation to the array with properly formatted properties
        annotationsArray.push({
          // Position properties
          x: backendX,
          y: backendY,
          width: backendWidth,
          height: backendHeight,
          page_no: pageNumInt,

          // Content properties
          text: annot.text,
          type: annot.type,
          imageUrl: imageUrlForMerge,
          checked: annot.checked,

          // Text formatting properties
          fontSize: annot.fontSize || 14,
          fontFamily: annot.fontFamily || "Arial",
          color: annot.color || "#000000",
          format: annot.format || [],
          letterSpacing: annot.letterSpacing || 0,
          alignment: annot.alignment,
          opacity: annot.opacity || 1,

          // Style properties
          backgroundColor: annot.backgroundColor,
          borderColor: annot.borderColor,

          // Signer properties
          signerId: annot.signerId,
          signerName: annot.signerName,
          signerColor: annot.signerColor,
          isRequired: annot.isRequired,
          fieldLabel: annot.fieldLabel,

          // Date specific properties
          dateFormat: annot.dateFormat,
          date: annot.date,

          // Dropdown specific properties
          options: annot.options,
          selectedOption: annot.selectedOption,

          // Signature/Image specific properties
          isSignature: annot.isSignature,
          aspectRatio: annot.aspectRatio,

          // Additional properties
          id: annot.id,
          scale: annot.scale,
        });
      });
    });
    console.log("annotationsArray", annotationsArray);
    console.log("annotations ", annotationsArray);
    // Create FormData
    const formData = new FormData();
    formData.append("pdfFile", pdfBlob, "original.pdf");
    // if (doc_completed === true) {
    //   console.log("activilth log ", ActivityLogData);
    //   formData.append("activityLog", ActivityLogData);
    // }

    formData.append(
      "annotations",
      JSON.stringify({
        annotations: annotationsArray,
        activityLog: doc_completed ? ActivityLogData : [],
        documentInfo: {
          uniqId: UniqIdDoc,
          fileName: fileName,
          logo: logo_data,
          user_id: user_id,
        },
      })
    );

    // Make API call
    const response = await fetch(`${BASE_URL}generate-annotated-pdf11`, {
      method: "POST",
      body: formData, // Don't set Content-Type header, let browser set it with boundary
    });
    if (!response.ok) {
      throw new Error("Failed to generate PDF");
    }
    // const blob = await response.blob();
    // const url = URL.createObjectURL(blob);
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = "annotated-document.pdf";
    // link.click();
    // URL.revokeObjectURL(url);

    const modifiedPdfBytes = await response.arrayBuffer();

    if (statusData === "receiver") {
      await uploadModifiedPdfReceiver(
        modifiedPdfBytes,
        file_id,
        user_id,
        fileName,
        noDec
      );
    } else {
      await uploadModifiedPdf(modifiedPdfBytes, file_id, user_id, fileName);
    }

    // downloadPDF(modifiedPdfBytes, fileName);

    return {
      error: false,
      message: "PDF generated and uploaded successfully",
    };
  } catch (error) {
    console.error("Error processing PDF:", error);
    return {
      error: true,
      message: error.message,
    };
  }
};
// template
export const handleDownloadPDFHereTemp = async ({
  setDownloadPdfLoader,
  imageUrls,
  textItems,
  canvasWidth,
  UniqIdDoc,
  ActivityLogData,
  fileName,
  file_id,
  statusData,
  imageUrlsCount,
  user_id,
  doc_completed,
  logo_data,
  noDec,
  // Optional: merged-PDF vertical nudge for my_text / signer_text only (px). Template signer completion uses a higher value.
  textBottomPaddingAbs: textBottomPaddingAbsParam,
}) => {
  try {
    console.log("Hanle pdf test  FFFHHHHHH");
    console.log("textItems", textItems);
    const pdfResponse = await fetch(imageUrls);
    if (!pdfResponse.ok) {
      console.error("Failed to fetch PDF:", pdfResponse.statusText);
      // Continue anyway with whatever response we got
    }
    const pdfArrayBuffer = await pdfResponse.arrayBuffer();
    const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

    // Build per-page visible box metrics (CropBox-aware) so backend route receives
    // coordinates in full-page percentages that match editor placement.
    const pageMetrics = {};
    try {
      const sourcePdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const sourcePages = sourcePdfDoc.getPages();
      sourcePages.forEach((page, index) => {
        const pageNum = index + 1;
        const { width: pageWidth, height: pageHeight } = page.getSize();
        let visibleBox = { x: 0, y: 0, width: pageWidth, height: pageHeight };
        try {
          if (typeof page.getCropBox === "function") {
            const cropBox = page.getCropBox();
            if (
              cropBox &&
              Number.isFinite(cropBox.x) &&
              Number.isFinite(cropBox.y) &&
              Number.isFinite(cropBox.width) &&
              Number.isFinite(cropBox.height) &&
              cropBox.width > 0 &&
              cropBox.height > 0
            ) {
              visibleBox = cropBox;
            }
          }
        } catch (_) {
          // Keep full page if CropBox read fails.
        }
        pageMetrics[pageNum] = {
          pageWidth,
          pageHeight,
          visibleBox,
        };
      });
    } catch (error) {
      console.warn("Failed to read source PDF page metrics, falling back to raw percentages:", error);
    }

    const annotationsArray = [];
    // Flatten the page-grouped annotations into a single array
    Object.entries(textItems).forEach(([pageNum, annotations]) => {
      const pageNumInt = parseInt(pageNum, 10);
      const metrics = pageMetrics[pageNumInt];
      annotations.forEach((annot) => {
        const rawX = Number(annot.x);
        const rawY = Number(annot.y);
        const rawWidth = Number(annot.width);
        const rawHeight = Number(annot.height);

        let backendX = rawX;
        let backendY = rawY;
        let backendWidth = rawWidth;
        let backendHeight = rawHeight;

        // Convert from visible-box percentages (editor) to full-page percentages (backend draw route).
        if (
          metrics &&
          Number.isFinite(rawX) &&
          Number.isFinite(rawY) &&
          Number.isFinite(rawWidth) &&
          Number.isFinite(rawHeight)
        ) {
          const { pageWidth, pageHeight, visibleBox } = metrics;
          if (pageWidth > 0 && pageHeight > 0) {
            const absLeft = visibleBox.x + (rawX / 100) * visibleBox.width;
            const absTop = visibleBox.y + visibleBox.height - (rawY / 100) * visibleBox.height;
            const absWidth = (rawWidth / 100) * visibleBox.width;
            const absHeight = (rawHeight / 100) * visibleBox.height;

            backendX = (absLeft / pageWidth) * 100;
            backendY = ((pageHeight - absTop) / pageHeight) * 100;
            backendWidth = (absWidth / pageWidth) * 100;
            backendHeight = (absHeight / pageHeight) * 100;
          }
        }

        // Pass signature/initial image positions through without any Y-shift.
        // The backend centers images in the annotation box; no pre-compensation needed.
        const bottomAlignImageTypes = [
          "my_signature",
          "my_initials",
          "signer_initials",
          "signer_initials_text",
        ];

        // Text-only vertical adjustment for merged/downloaded PDF:
        // positive textBottomPaddingAbs should move text UP from bottom line.
        if (
          annot.type === "my_text" ||
          annot.type === "signer_text" ||
          annot.type === "date" ||
          annot.type === "signer_date"
        ) {
          const textPaddingPx = Number.isFinite(textBottomPaddingAbsParam)
            ? textBottomPaddingAbsParam
            : 2.5; // px on PDF page space
          const pageHeightForNudge = metrics?.pageHeight || 1;
          const textBottomPaddingPercent =
            (textPaddingPx / pageHeightForNudge) * 100;
          backendY = Math.min(
            100,
            Math.max(0, backendY - textBottomPaddingPercent)
          );
        }

        const imageUrlForMerge =
          bottomAlignImageTypes.includes(annot.type) && annot.croppedImageData
            ? annot.croppedImageData
            : annot.imageUrl;

        // Add each annotation to the array with properly formatted properties
        annotationsArray.push({
          // Position properties
          x: backendX,
          y: backendY,
          width: backendWidth,
          height: backendHeight,
          page_no: pageNumInt,

          // Content properties
          text: annot.text,
          type: annot.type,
          imageUrl: imageUrlForMerge,
          checked: annot.checked,

          // Text formatting properties
          fontSize: annot.fontSize || 14,
          fontFamily: annot.fontFamily || "Arial",
          color: annot.color || "#000000",
          format: annot.format || [],
          letterSpacing: annot.letterSpacing || 0,
          alignment: annot.alignment,
          opacity: annot.opacity || 1,

          // Style properties
          backgroundColor: annot.backgroundColor,
          borderColor: annot.borderColor,

          // Signer properties
          signerId: annot.signerId,
          signerName: annot.signerName,
          signerColor: annot.signerColor,
          isRequired: annot.isRequired,
          fieldLabel: annot.fieldLabel,

          // Date specific properties
          dateFormat: annot.dateFormat,
          date: annot.date,

          // Dropdown specific properties
          options: annot.options,
          selectedOption: annot.selectedOption,

          // Signature/Image specific properties
          isSignature: annot.isSignature,
          aspectRatio: annot.aspectRatio,

          // Additional properties
          id: annot.id,
          scale: annot.scale,
        });
      });
    });
    console.log("annotationsArray", annotationsArray);
    console.log("annotations ", annotationsArray);
    // Create FormData
    const formData = new FormData();
    formData.append("pdfFile", pdfBlob, "original.pdf");
    // if (doc_completed === true) {
    //   console.log("activilth log ", ActivityLogData);
    //   formData.append("activityLog", ActivityLogData);
    // }

    formData.append(
      "annotations",
      JSON.stringify({
        annotations: annotationsArray,
        activityLog: doc_completed ? ActivityLogData : [],
        documentInfo: {
          uniqId: UniqIdDoc,
          fileName: fileName,
          logo: logo_data,
          user_id: user_id,
        },
      })
    );

    // Make API call
    const response = await fetch(`${BASE_URL}generate-annotated-pdf11`, {
      method: "POST",
      body: formData, // Don't set Content-Type header, let browser set it with boundary
    });
    if (!response.ok) {
      throw new Error("Failed to generate PDF");
    }
    // const blob = await response.blob();
    // const url = URL.createObjectURL(blob);
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = "annotated-document.pdf";
    // link.click();
    // URL.revokeObjectURL(url);

    const modifiedPdfBytes = await response.arrayBuffer();
    let returningdata;
    if (statusData === "receiver") {
      console.log("receibver data ");
      returningdata = await updateTemplatePositionsReceiver(
        modifiedPdfBytes,
        file_id,
        user_id,
        fileName
      );
      return returningdata;
    } else {
      await updateTemplatePositions(
        modifiedPdfBytes,
        file_id,
        user_id,
        fileName
      );
    }

    // downloadPDF(modifiedPdfBytes, fileName);

    return {
      error: false,
      message: "PDF generated and uploaded successfully",
    };
  } catch (error) {
    console.error("Error processing PDF:", error);
    return {
      error: true,
      message: error.message,
    };
  }
};
export const handleDownloadPDFHereBulk = async ({
  setDownloadPdfLoader,
  imageUrls,
  textItems,
  canvasWidth,
  UniqIdDoc,
  ActivityLogData,
  fileName,
  file_id,
  statusData,
  imageUrlsCount,
  user_id,
  doc_completed,
  logo_data,
  noDec,
}) => {
  try {
    console.log("Hanle pdf test  FFFHHHHHH");
    console.log("textItems", textItems);
    const annotationsArray = [];
    // Flatten the page-grouped annotations into a single array
    Object.entries(textItems).forEach(([pageNum, annotations]) => {
      const imageElem = document.querySelector(`#img-${pageNum} .pdf-image`);
      const imageRect = imageElem.getBoundingClientRect();
      annotations.forEach((annot) => {
        let mergedY = annot.y;
        if (annot.type === "my_text" || annot.type === "signer_text") {
          // Bulk/public path uses current page DOM metrics; nudge text up similarly.
          const textBottomPaddingAbs = 2.5;
          const textBottomPaddingPercent = (textBottomPaddingAbs / imageRect.height) * 100;
          mergedY = Math.min(100, mergedY + textBottomPaddingPercent);
        }
        const xPercent = (annot.x / imageRect.width) * 100;
        const yPercent = (annot.y / imageRect.height) * 100;
        // Add each annotation to the array with properly formatted properties
        annotationsArray.push({
          // Position properties
          x: annot.x,
          y: mergedY,
          width: annot.width,
          height: annot.height,
          page_no: parseInt(pageNum),

          // Content properties
          text: annot.text,
          type: annot.type,
          imageUrl: annot.imageUrl,
          checked: annot.checked,

          // Text formatting properties
          fontSize: annot.fontSize || 14,
          fontFamily: annot.fontFamily || "Arial",
          color: annot.color || "#000000",
          format: annot.format || [],
          letterSpacing: annot.letterSpacing || 0,
          alignment: annot.alignment,
          opacity: annot.opacity || 1,

          // Style properties
          backgroundColor: annot.backgroundColor,
          borderColor: annot.borderColor,

          // Signer properties
          signerId: annot.signerId,
          signerName: annot.signerName,
          signerColor: annot.signerColor,
          isRequired: annot.isRequired,
          fieldLabel: annot.fieldLabel,

          // Date specific properties
          dateFormat: annot.dateFormat,
          date: annot.date,

          // Dropdown specific properties
          options: annot.options,
          selectedOption: annot.selectedOption,

          // Signature/Image specific properties
          isSignature: annot.isSignature,
          aspectRatio: annot.aspectRatio,

          // Additional properties
          id: annot.id,
          scale: annot.scale,
        });
      });
    });
    console.log("annotationsArray", annotationsArray);
    const pdfResponse = await fetch(imageUrls);
    const pdfBlob = await pdfResponse.blob();
    console.log("annotations ", annotationsArray);
    // Create FormData
    const formData = new FormData();
    formData.append("pdfFile", pdfBlob, "original.pdf");
    // if (doc_completed === true) {
    //   console.log("activilth log ", ActivityLogData);
    //   formData.append("activityLog", ActivityLogData);
    // }

    formData.append(
      "annotations",
      JSON.stringify({
        annotations: annotationsArray,
        activityLog: doc_completed ? ActivityLogData : [],
        documentInfo: {
          uniqId: UniqIdDoc,
          fileName: fileName,
          logo: logo_data,
          user_id: user_id,
        },
      })
    );

    // Make API call
    const response = await fetch(`${BASE_URL}generate-annotated-pdf11`, {
      method: "POST",
      body: formData, // Don't set Content-Type header, let browser set it with boundary
    });
    if (!response.ok) {
      throw new Error("Failed to generate PDF");
    }
    // const blob = await response.blob();
    // const url = URL.createObjectURL(blob);
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = "annotated-document.pdf";
    // link.click();
    // URL.revokeObjectURL(url);

    const modifiedPdfBytes = await response.arrayBuffer();
    let returningdata;
    if (statusData === "receiver") {
      console.log("receibver data ");
      returningdata = await updateBulkLinkPositionsReceiver(
        modifiedPdfBytes,
        file_id,
        user_id,
        fileName
      );
      return returningdata;
    } else {
      await updateBulkLinkPositions(
        modifiedPdfBytes,
        file_id,
        user_id,
        fileName
      );
    }

    // downloadPDF(modifiedPdfBytes, fileName);

    return {
      error: false,
      message: "PDF generated and uploaded successfully",
    };
  } catch (error) {
    console.error("Error processing PDF:", error);
    return {
      error: true,
      message: error.message,
    };
  }
};
const updateBulkLinkPositions = async (
  pdfBytes,
  file_id,
  user_id,
  fileName
) => {
  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log(file);
  const postData = {
    image: file,
    user_id: user_id,
  };

  try {
    const apiData = await postFormDataPdf(postData, (progress) => {
      console.log(progress);
    });
    console.log(apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      const postData1 = {
        bulk_link_id: file_id,
        url: apiData.public_url,
        file_name: fileName,
      };
      console.log("apiData.public_url");

      console.log(apiData.public_url);
      const apiData2 = await post("bulk_links/updateBulkLink", postData1);
      console.log("apiData2");

      console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
  // const formData = new FormData();
  // formData.append('file', blob, 'modified.pdf');

  // // Assuming you have an API endpoint to upload the file
  // const response = await fetch(`${BASE_URL}/upload`, {
  //   method: 'POST',
  //   body: formData,
  // });

  // if (!response.ok) {
  //   throw new Error('Failed to upload the modified PDF');
  // }
};
const updateBulkLinkPositionsReceiver = async (
  pdfBytes,
  file_id,
  user_id,
  fileName
) => {
  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log(file);
  const postData = {
    image: file,
    user_id: user_id,
  };

  try {
    const apiData = await postFormDataPdf(postData, (progress) => {
      console.log(progress);
    });
    console.log(apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      const postData1 = {
        file_id: file_id,
        image: apiData.public_url,
      };
      console.log("apiData.public_url");

      console.log(apiData.public_url);
      return apiData.public_url;
      // const apiData2 = await post("template/updatebgimgs", postData1);
      // console.log("apiData2");

      // console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};

const updateTemplatePositions = async (
  pdfBytes,
  file_id,
  user_id,
  fileName
) => {
  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log(file);
  const postData = {
    image: file,
    user_id: user_id,
  };

  try {
    const apiData = await postFormDataPdf(postData, (progress) => {
      console.log(progress);
    });
    console.log(apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      const postData1 = {
        file_id: file_id,
        image: apiData.public_url,
      };
      console.log("apiData.public_url");

      console.log(apiData.public_url);
      const apiData2 = await post("template/updatebgimgs", postData1);
      console.log("apiData2");

      console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
  // const formData = new FormData();
  // formData.append('file', blob, 'modified.pdf');

  // // Assuming you have an API endpoint to upload the file
  // const response = await fetch(`${BASE_URL}/upload`, {
  //   method: 'POST',
  //   body: formData,
  // });

  // if (!response.ok) {
  //   throw new Error('Failed to upload the modified PDF');
  // }
};
export const updateTemplatePositionsReceiver = async (
  pdfBytes,
  file_id,
  user_id,
  fileName
) => {
  console.log("uploadModifiedPdfReceiver");

  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log("file RECEIUVER");

  console.log(file);
  console.log(user_id);

  const postData = {
    image: file,
    user_id: user_id,
    // file_id: file_id,
  };

  try {
    const apiData = await postFormDataPdf(postData, (progress) => {
      console.log(progress);
    });
    console.log(apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      const postData1 = {
        file_id: file_id,
        image: apiData.public_url,
      };
      console.log("apiData.public_url");

      console.log(apiData.public_url);
      return apiData.public_url;
      // const apiData2 = await post("template/updatebgimgs", postData1);
      // console.log("apiData2");

      // console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};
const downloadPDF = (pdfBytes, fileName) => {
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
export const addLogoActivityToPdfBytes = async ({
  pdfBytes,
  uniqId,
  fileName,
  logo,
  activityLog,
  user_id,
}) => {
  try {
    const formData = new FormData();
    const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
    formData.append("pdfFile", pdfBlob, fileName || "annotated-document.pdf");
    formData.append(
      "documentInfo",
      JSON.stringify({
        uniqId,
        fileName,
        logo,
        user_id: user_id || undefined,
      })
    );
    formData.append("activityLog", JSON.stringify(activityLog || []));

    const response = await fetch(`${BASE_URL}add-logo-activity-pdf`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(
        `Failed to append activity log: ${response.status} ${errText}`
      );
    }

    return await response.arrayBuffer();
  } catch (error) {
    console.error("Error adding logo/activity to PDF bytes:", error);
    throw error;
  }
};
export const uploadModifiedPdfReceiver = async (
  pdfBytes,
  file_id,
  user_id,
  fileName,
  noDec
) => {
  console.log("uploadModifiedPdfReceiver");
  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log(file);
  const postData = {
    image: file,
    user_id: user_id,
    file_id: file_id,
    noDec: noDec,
  };

  try {
    const apiData = await postFormDataPdfReplace(postData, (progress) => {
      console.log(progress);
    });
    console.log("apisdd", apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      let postData1;
      if (noDec === true) {
        postData1 = {
          file_id: file_id,
          image: apiData.public_url,
          noDec: true,
        };
      } else {
        postData1 = {
          file_id: file_id,
          image: apiData.public_url,
        };
      }
      console.log(postData1);
      // console.log("apiData.path");

      console.log(apiData.public_url);
      const apiData2 = await post("file/updatebgimgsReceiver", postData1);
      console.log("apiData2");

      console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};
const uploadModifiedPdf = async (pdfBytes, file_id, user_id, fileName) => {
  const file = new File([pdfBytes], ensurePdfFileName(fileName), {
    type: "application/pdf",
  });
  console.log(file);
  const postData = {
    image: file,
    user_id: user_id,
  };

  try {
    const apiData = await postFormDataPdf(postData, (progress) => {
      console.log(progress);
    });
    console.log(apiData);
    if (apiData.error === true || apiData.error === "true") {
      console.log("Error Downloading and uploading Files");
    } else {
      const postData1 = {
        file_id: file_id,
        image: apiData?.public_url,
      };
      console.log("apiData.path");

      console.log(apiData.public_url);
      const apiData2 = await post("file/updatebgimgs", postData1);
      console.log("apiData2");

      console.log(apiData2);
    }
  } catch (error) {
    console.error("Error uploading file:", error);
  }
  // const formData = new FormData();
  // formData.append('file', blob, 'modified.pdf');

  // // Assuming you have an API endpoint to upload the file
  // const response = await fetch(`${BASE_URL}/upload`, {
  //   method: 'POST',
  //   body: formData,
  // });

  // if (!response.ok) {
  //   throw new Error('Failed to upload the modified PDF');
  // }
};
export const getSignerBoxContent = (type) => {
  switch (type) {
    case "signer_initials":
      return {
        icon: <EditIcon sx={{ fontSize: window.innerWidth < 780 ? 5 : 20 }} />,
        text: "Sign",
      };
    case "signer_initials_text":
      return {
        icon: <EditIcon sx={{ fontSize: window.innerWidth < 780 ? 5 : 20 }} />,
        text: "Initials",
      };
    case "signer_chooseImgStamp":
      return {
        icon: (
          <ApprovalIcon sx={{ fontSize: window.innerWidth < 780 ? 5 : 20 }} />
        ),
        text: "Stamp",
      };
    case "signer_chooseImgDrivingL":
      return {
        icon: (
          <LicenseIcon sx={{ fontSize: window.innerWidth < 780 ? 5 : 20 }} />
        ),
        text: "Driving License",
      };
    case "signer_chooseImgPassportPhoto":
      return {
        icon: <ImageIcon sx={{ fontSize: window.innerWidth < 780 ? 5 : 20 }} />,
        text: "Passport Photo",
      };
    default:
      return null;
  }
};
export const getSignerBoxContentSvg = (type) => {
  switch (type) {
    case "signer_initials":
      return {
        icon: `
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000003"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
        `,
        text: "Sign",
      };
    case "signer_initials_text":
      return {
        icon: `
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000003"><path d="M200-200v-100h160l120-360H320v-100h400v100H580L460-300h140v100H200Z"/></svg>
        `,
        text: "Initials",
      };
    case "signer_chooseImgStamp":
      return {
        icon: `
         <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000003"><path d="M160-80v-240q0-33 23.5-56.5T240-400h480q33 0 56.5 23.5T800-320v240H160Zm80-160h480v-80H240v80Zm240-160L280-680q0-83 58.5-141.5T480-880q83 0 141.5 58.5T680-680L480-400Zm0-112 120-168q0-50-35-85t-85-35q-50 0-85 35t-35 85l120 168Zm0-144Z"/></svg>
        `,
        text: "Stamp",
      };
    case "signer_chooseImgDrivingL":
      return {
        icon: `
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000003"><path d="M560-440h200v-80H560v80Zm0-120h200v-80H560v80ZM200-320h320v-22q0-45-44-71.5T360-440q-72 0-116 26.5T200-342v22Zm160-160q33 0 56.5-23.5T440-560q0-33-23.5-56.5T360-640q-33 0-56.5 23.5T280-560q0 33 23.5 56.5T360-480ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z"/></svg>
        `,
        text: "Driving License",
      };
    case "signer_chooseImgPassportPhoto":
      return {
        icon: `
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000003"><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm40-80h480L570-480 450-320l-90-120-120 160Zm-40 80v-560 560Z"/></svg>
        `,
        text: "Passport Photo",
      };
    default:
      return null;
  }
};
